import { useState, useEffect } from 'react'
import { monitoringService, WorkerStatus } from '@/services/monitoring'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Loader2, Heart, Server, Database, Circle, CheckCircle2, XCircle, Activity, Clock, TrendingUp } from 'lucide-react'

export function SystemHealth() {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<WorkerStatus | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date())

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        setError(null)

        const response = await monitoringService.getWorkersStatus()
        setData(response)
        setLastUpdate(new Date())
      } catch (err: any) {
        console.error('Failed to fetch system health:', err)
        setError(err.response?.data?.detail || 'Failed to load system health data')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
    // Poll every 15 seconds for real-time updates
    const interval = setInterval(fetchData, 15000)

    return () => clearInterval(interval)
  }, [])

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <XCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <p className="text-lg font-semibold">Failed to load system health</p>
          <p className="text-sm text-muted-foreground mt-2">{error}</p>
        </div>
      </div>
    )
  }

  if (!data) {
    return null
  }

  const isHealthy = data.status === 'healthy'
  const isDegraded = data.status === 'degraded'

  return (
    <div className="space-y-6">
      {/* Header with System Status */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-semibold flex items-center gap-2">
            <Heart className="h-6 w-6" />
            System Health
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Real-time monitoring of Celery workers, queues, and infrastructure components
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">
            Last updated: {lastUpdate.toLocaleTimeString('en-US', { timeZone: 'UTC', hour: '2-digit', minute: '2-digit' })} UTC
          </span>
          {isHealthy ? (
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
              All Systems Operational
            </Badge>
          ) : isDegraded ? (
            <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
              <XCircle className="h-3.5 w-3.5 mr-1" />
              System Degraded
            </Badge>
          ) : (
            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
              <XCircle className="h-3.5 w-3.5 mr-1" />
              System Critical
            </Badge>
          )}
        </div>
      </div>

      {/* Component Health Indicators */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Database className="h-4 w-4" />
            Infrastructure Components
          </CardTitle>
          <CardDescription>Health status of core data pipeline components</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* PostgreSQL */}
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-2">
                <Database className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="font-medium text-sm">PostgreSQL</p>
                  <p className="text-xs text-muted-foreground">Primary DB</p>
                </div>
              </div>
              {data.components.postgres ? (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              ) : (
                <XCircle className="h-5 w-5 text-red-600" />
              )}
            </div>

            {/* ClickHouse */}
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-2">
                <Database className="h-5 w-5 text-orange-600" />
                <div>
                  <p className="font-medium text-sm">ClickHouse</p>
                  <p className="text-xs text-muted-foreground">Analytics DB</p>
                </div>
              </div>
              {data.components.clickhouse ? (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              ) : (
                <XCircle className="h-5 w-5 text-red-600" />
              )}
            </div>

            {/* Redis */}
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-2">
                <Server className="h-5 w-5 text-red-600" />
                <div>
                  <p className="font-medium text-sm">Redis</p>
                  <p className="text-xs text-muted-foreground">Cache & Queue</p>
                </div>
              </div>
              {data.components.redis ? (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              ) : (
                <XCircle className="h-5 w-5 text-red-600" />
              )}
            </div>

            {/* Celery */}
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-green-600" />
                <div>
                  <p className="font-medium text-sm">Celery</p>
                  <p className="text-xs text-muted-foreground">Task Queue</p>
                </div>
              </div>
              {data.components.celery ? (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              ) : (
                <XCircle className="h-5 w-5 text-red-600" />
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Success Rate */}
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>24h Success Rate</CardDescription>
            <CardTitle className="text-3xl flex items-baseline gap-2">
              {data.metrics.success_rate_24h.toFixed(1)}%
              {data.metrics.success_rate_24h >= 90 ? (
                <TrendingUp className="h-5 w-5 text-green-600" />
              ) : (
                <Circle className="h-5 w-5 text-yellow-600" />
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">Last 24 hours</p>
          </CardContent>
        </Card>

        {/* Avg Collect Duration */}
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Avg Collect</CardDescription>
            <CardTitle className="text-3xl flex items-baseline gap-2">
              <Clock className="h-5 w-5 text-blue-600 mr-1" />
              {data.metrics.avg_collect_duration >= 60
                ? `${(data.metrics.avg_collect_duration / 60).toFixed(1)}m`
                : `${data.metrics.avg_collect_duration.toFixed(0)}s`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">Average duration</p>
          </CardContent>
        </Card>

        {/* Avg Load Duration */}
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Avg Load</CardDescription>
            <CardTitle className="text-3xl flex items-baseline gap-2">
              <Clock className="h-5 w-5 text-purple-600 mr-1" />
              {data.metrics.avg_load_duration >= 60
                ? `${(data.metrics.avg_load_duration / 60).toFixed(1)}m`
                : `${data.metrics.avg_load_duration.toFixed(0)}s`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">Average duration</p>
          </CardContent>
        </Card>

        {/* Avg Push Duration */}
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Avg Push</CardDescription>
            <CardTitle className="text-3xl flex items-baseline gap-2">
              <Clock className="h-5 w-5 text-orange-600 mr-1" />
              {data.metrics.avg_push_duration >= 60
                ? `${(data.metrics.avg_push_duration / 60).toFixed(1)}m`
                : `${data.metrics.avg_push_duration.toFixed(0)}s`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">Average duration</p>
          </CardContent>
        </Card>
      </div>

      {/* Queue Depths with Workers */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Queue Depths & Workers
          </CardTitle>
          <CardDescription>
            Pending tasks in Redis queues and {data.workers.length} active worker{data.workers.length !== 1 ? 's' : ''} (Push uses Default queue)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {data.queues.map((queue) => {
              const maxDepth = 1000
              const progressPct = Math.min((queue.depth / maxDepth) * 100, 100)
              const isHigh = queue.depth > 500
              const isCritical = queue.depth > 800
              // Get workers for this queue
              const queueWorkers = data.workers.filter(w => w.queue === queue.name)

              return (
                <div key={queue.name} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm capitalize">{queue.name}</p>
                      <Badge variant="outline" className="text-xs">
                        {queue.consumers} worker{queue.consumers !== 1 ? 's' : ''}
                      </Badge>
                    </div>
                    <p className={`text-sm font-semibold ${
                      isCritical ? 'text-red-600' : isHigh ? 'text-yellow-600' : 'text-muted-foreground'
                    }`}>
                      {queue.depth.toLocaleString()} pending
                    </p>
                  </div>
                  <Progress
                    value={progressPct}
                    className={`h-2 ${
                      isCritical ? '[&>div]:bg-red-600' : isHigh ? '[&>div]:bg-yellow-500' : '[&>div]:bg-green-600'
                    }`}
                  />
                  {/* Workers for this queue */}
                  {queueWorkers.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      {queueWorkers
                        .sort((a, b) => {
                          const numA = parseInt(a.name.split('@')[0].split('-').pop() || '0')
                          const numB = parseInt(b.name.split('@')[0].split('-').pop() || '0')
                          return numA - numB
                        })
                        .map((worker) => {
                        // Extract just the number from worker name (e.g., "load-6@sentinel" -> "6")
                        const workerNum = worker.name.split('@')[0].split('-').pop() || worker.name

                        return (
                          <Badge
                            key={worker.name}
                            variant={worker.active_tasks > 0 ? 'default' : 'outline'}
                            className={`text-xs font-medium min-w-[24px] justify-center ${
                              worker.active_tasks > 0
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'bg-white dark:bg-gray-900 border-gray-300'
                            }`}
                            title={`${worker.name}: ${worker.active_tasks} active task${worker.active_tasks !== 1 ? 's' : ''}`}
                          >
                            {workerNum}
                            {worker.active_tasks > 0 && (
                              <span className="ml-0.5">({worker.active_tasks})</span>
                            )}
                          </Badge>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
